import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Divider,
  Paper,
  Switch,
  FormControlLabel,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  CheckCircle,
  Star,
  TrendingUp,
  Security,
  Language,
  Payment,
  Info,
  Close,
  Verified,
  Public,
  CurrencyRupee,
  AttachMoney,
  Euro,
  CurrencyPound
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import EnhancedPayment from './EnhancedPayment';

const EnhancedPricingPage = ({ onClose }) => {
  const { API_URL } = require('../utils/apiConfig');
  const { user, token } = useAuth();

  // State management
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [isAnnual, setIsAnnual] = useState(false);
  const [error, setError] = useState('');
  const [loginRequired, setLoginRequired] = useState(false);
  const [success, setSuccess] = useState('');
  
  // Payment dialog
  const [paymentDialog, setPaymentDialog] = useState({ open: false, plan: null });
  
  // Regional data
  const [userLocation, setUserLocation] = useState(null);
  const [exchangeRates, setExchangeRates] = useState({});
  const errorAlertRef = useRef(null);

  useEffect(() => {
    if (!loginRequired || !error) return;
    const frame = requestAnimationFrame(() => {
      errorAlertRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      errorAlertRef.current?.focus?.({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [loginRequired, error]);

  useEffect(() => {
    loadPricingData();
    if (user) {
      loadCurrentSubscription();
    }
  }, [user, selectedCurrency, isAnnual]);

  const loadPricingData = async () => {
    try {
      setLoading(true);
      
      // Load available currencies and user location
      const methodsResponse = await fetch('/api/payments/methods', {
        headers: token ? {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        } : { 'Content-Type': 'application/json' }
      });
      const methodsData = await methodsResponse.json();
      
      if (methodsData.success) {
        // Prefer the full supportedCurrencies list (includes INR even if some gateways are disabled)
        const supported = Array.isArray(methodsData.supportedCurrencies)
          ? methodsData.supportedCurrencies
          : [...new Set(methodsData.methods.flatMap(m => m.currencies))];

        setAvailableCurrencies(supported);

        // Set default currency based on recommendation
        const defaultCurrency = methodsData.recommendedCurrency || 'USD';
        if (!selectedCurrency || selectedCurrency === 'USD') {
          setSelectedCurrency(defaultCurrency);
        }

        // Fetch userLocation and exchangeRates from currencies endpoint
        try {
          const currenciesResp = await fetch('/api/payments/currencies', {
            headers: token ? {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            } : { 'Content-Type': 'application/json' }
          });
          const currenciesData = await currenciesResp.json();
          if (currenciesData.success) {
            setUserLocation(currenciesData.userLocation);
            setExchangeRates(currenciesData.exchangeRates || {});
          }
        } catch (e) {
          console.warn('Unable to fetch currencies info:', e);
        }
      }

      // Load plans with pricing
      const plansResponse = await fetch('/api/subscriptions/plans', {
        headers: token ? {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        } : { 'Content-Type': 'application/json' }
      });
      const plansData = await plansResponse.json();
      
      if (plansData.success) {
        // Load regional pricing for each plan
        const plansWithPricing = await Promise.all(
          plansData.plans.map(async (plan) => {
            try {
              const pricingResponse = await fetch(
                `/api/payments/pricing/${plan.id}?currency=${selectedCurrency}&interval=${isAnnual ? 'yearly' : 'monthly'}`,
                {
                  headers: token ? {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  } : { 'Content-Type': 'application/json' }
                }
              );
              const pricingData = await pricingResponse.json();
              
              // Compute a simple pricing object expected by UI from regionalPricing map
              let computedPricing = null;
              if (pricingData.success) {
                const regional = pricingData.regionalPricing || {};
                const userCountry = userLocation?.country || 'US';
                // Prefer price entry matching selected currency and user country
                const entries = Object.entries(regional).filter(([_, info]) => info.currency === selectedCurrency);
                let chosen = null;
                if (entries.length > 0) {
                  // If user's country matches selected currency, pick that; else first match
                  const byUserCountry = entries.find(([code]) => code === userCountry);
                  chosen = byUserCountry ? byUserCountry[1] : entries[0][1];
                } else {
                  // Fallback: use user's country pricing or US
                  chosen = regional[userCountry] || regional['US'] || null;
                }

                if (chosen) {
                  const monthly = Number(chosen.price) || 0;
                  const annual = Math.round(monthly * 12 * 0.8 * 100) / 100; // up to 20% savings
                  const amount = isAnnual ? annual : monthly;
                  const discount = isAnnual && monthly > 0 ? 20 : 0;
                  computedPricing = {
                    amount,
                    currency: selectedCurrency,
                    interval: isAnnual ? 'yearly' : 'monthly',
                    discount,
                    isRegionalPricing: chosen.multiplier !== 1 || selectedCurrency !== 'USD'
                  };
                }
              }
              
              return {
                ...plan,
                pricing: computedPricing
              };
            } catch (error) {
              console.error(`Error loading pricing for plan ${plan.id}:`, error);
              return { ...plan, pricing: null };
            }
          })
        );
        
        setPlans(plansWithPricing);
      }
    } catch (error) {
      console.error('Error loading pricing data:', error);
      setLoginRequired(false);
      setError('Failed to load pricing information');
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentSubscription = async () => {
    try {
      const response = await fetch('/api/subscriptions/current', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setCurrentSubscription(data.subscription);
      }
    } catch (error) {
      console.error('Error loading current subscription:', error);
    }
  };

  const handleSubscribe = (plan) => {
    if (!user) {
      setLoginRequired(true);
      setError('Please log in to subscribe to a plan');
      return;
    }

    setLoginRequired(false);
    setPaymentDialog({ open: true, plan });
  };

  const clearError = () => {
    setError('');
    setLoginRequired(false);
  };

  const handlePaymentSuccess = (paymentData) => {
    // Clear focus from any focused element to prevent aria-hidden accessibility issues
    if (document.activeElement && document.activeElement.blur) {
      document.activeElement.blur();
    }

    // Use setTimeout to allow focus to be properly managed before closing dialog
    setTimeout(() => {
      setPaymentDialog({ open: false, plan: null });

      // If the verify-payment response already contains the new subscription,
      // update the UI immediately (no stale plan shown while webhook catches up).
      if (paymentData.subscription) {
        setCurrentSubscription(paymentData.subscription);
      }

      // Handle different success scenarios
      if (paymentData.fallback) {
        setSuccess('Payment processed successfully! Subscription activation may take a few minutes.');
        console.log('Fallback payment success:', paymentData);
      } else {
        const planLabel = paymentData.subscription?.plan_name || 'new';
        setSuccess(`🎉 ${planLabel} plan activated successfully! Enjoy your new features.`);
      }

      // Also re-fetch from the server after a short delay so that once
      // the webhook promotes the subscription to 'active' the UI reflects it.
      loadCurrentSubscription();
      setTimeout(loadCurrentSubscription, 5000);
    }, 100);
  };

  const formatCurrency = (amount, currency) => {
    if (!amount) return 'Free';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: currency === 'JPY' ? 0 : 2
    }).format(amount);
  };

  const getCurrencyIcon = (currency) => {
    switch (currency) {
      case 'INR': return <CurrencyRupee />;
      case 'USD': return <AttachMoney />;
      case 'EUR': return <Euro />;
      case 'GBP': return <CurrencyPound />;
      default: return <AttachMoney />;
    }
  };

  const getPlanIcon = (planName) => {
    switch (planName.toLowerCase()) {
      case 'free': return <Security color="primary" sx={{ fontSize: 40 }} />;
      case 'pro': case 'premium': return <Star color="secondary" sx={{ fontSize: 40 }} />;
      case 'enterprise': return <TrendingUp color="success" sx={{ fontSize: 40 }} />;
      default: return <Security color="primary" sx={{ fontSize: 40 }} />;
    }
  };

  const getPlanColor = (planName) => {
    switch (planName.toLowerCase()) {
      case 'free': return 'primary';
      case 'pro': case 'premium': return 'secondary';
      case 'enterprise': return 'success';
      default: return 'primary';
    }
  };

  const isCurrentPlan = (planId) => {
    return currentSubscription && currentSubscription.planId === planId;
  };

  const getDiscountPercentage = (monthlyPrice, annualPrice) => {
    if (!monthlyPrice || !annualPrice) return 0;
    const annualMonthly = annualPrice / 12;
    return Math.round(((monthlyPrice - annualMonthly) / monthlyPrice) * 100);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
          Choose Your Plan
        </Typography>
        <Typography variant="h5" color="text.secondary" paragraph>
          Flexible pricing for every need, with global payment support
        </Typography>
        
        {/* Currency and Billing Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3, mt: 4 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Currency</InputLabel>
            <Select
              value={selectedCurrency}
              onChange={(e) => setSelectedCurrency(e.target.value)}
              label="Currency"
              startAdornment={getCurrencyIcon(selectedCurrency)}
            >
              {availableCurrencies.map(currency => (
                <MenuItem key={currency} value={currency}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getCurrencyIcon(currency)}
                    {currency}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControlLabel
            control={
              <Switch
                checked={isAnnual}
                onChange={(e) => setIsAnnual(e.target.checked)}
                color="secondary"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography>Annual Billing</Typography>
                <Chip label="Save up to 20%" color="success" size="small" />
              </Box>
            }
          />
        </Box>

        {/* Location Info */}
        {userLocation && (
          <Alert severity="info" sx={{ mt: 3, maxWidth: 600, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Public fontSize="small" />
              <Typography variant="body2">
                Pricing optimized for {userLocation.country} • Currency: {selectedCurrency}
              </Typography>
            </Box>
          </Alert>
        )}
      </Box>

      {/* Error/Success Messages */}
      {error && (
        <Box
          ref={errorAlertRef}
          tabIndex={-1}
          sx={{ mb: 3, scrollMarginTop: { xs: 88, md: 96 }, outline: 'none' }}
        >
          <Alert
            severity="error"
            onClose={clearError}
            action={
              loginRequired ? (
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  component={RouterLink}
                  to="/login?redirect=%2Fpricing"
                  sx={{ borderColor: 'currentColor', whiteSpace: 'nowrap' }}
                >
                  Log In
                </Button>
              ) : undefined
            }
          >
            {error}
          </Alert>
        </Box>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Pricing Cards */}
      <Grid container spacing={4} justifyContent="center">
        {plans.map((plan) => {
          const isPopular = plan.name.toLowerCase() === 'pro' || plan.name.toLowerCase() === 'premium';
          const isCurrent = isCurrentPlan(plan.id);
          const pricing = plan.pricing;
          
          return (
            <Grid item xs={12} md={4} key={plan.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  border: isPopular ? 2 : 1,
                  borderColor: isPopular ? 'secondary.main' : 'divider',
                  transform: isPopular ? 'scale(1.05)' : 'none',
                  boxShadow: isPopular ? 8 : 2,
                  '&:hover': {
                    boxShadow: isPopular ? 12 : 6,
                    transform: isPopular ? 'scale(1.05)' : 'scale(1.02)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {/* Popular Badge */}
                {isPopular && (
                  <Chip
                    label="Most Popular"
                    color="secondary"
                    sx={{
                      position: 'absolute',
                      top: -10,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      fontWeight: 'bold'
                    }}
                  />
                )}
                
                {/* Current Plan Badge */}
                {isCurrent && (
                  <Chip
                    label="Current Plan"
                    color="success"
                    variant="outlined"
                    sx={{
                      position: 'absolute',
                      top: isPopular ? 20 : -10,
                      right: 16,
                      fontWeight: 'bold'
                    }}
                  />
                )}

                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  {/* Plan Header */}
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    {getPlanIcon(plan.name)}
                    <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mt: 1 }}>
                      {plan.name}
                    </Typography>
                    
                    {/* Pricing */}
                    <Box sx={{ mb: 2 }}>
                      {pricing && pricing.amount > 0 ? (
                        <>
                          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 1 }}>
                            <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                              {formatCurrency(pricing.amount, selectedCurrency)}
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                              /{pricing.interval}
                            </Typography>
                          </Box>
                          
                          {/* Annual Discount */}
                          {isAnnual && pricing.discount > 0 && (
                            <Chip 
                              label={`${pricing.discount}% OFF`} 
                              color="success" 
                              size="small" 
                              sx={{ mt: 1 }}
                            />
                          )}
                          
                          {/* Regional Pricing Notice */}
                          {pricing.isRegionalPricing && (
                            <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                              ✓ Regional pricing applied
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="h3" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                          Free
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Features List */}
                  <List dense>
                    {(() => {
                      // --- Limit key → human-readable label mapping ---
                      const limitLabelMap = {
                        interviewsPerMonth:    (v) => v === -1 ? 'Unlimited AI interviews/month'      : `${v} AI interview${v !== 1 ? 's' : ''}/month`,
                        resumeUploads:         (v) => v === -1 ? 'Unlimited resume uploads'           : `${v} resume upload${v !== 1 ? 's' : ''}`,
                        resumeAnalyses:        (v) => v === -1 ? 'Unlimited resume analyses'          : `${v} resume analys${v !== 1 ? 'es' : 'is'}`,
                        p2pSessionsPerMonth:   (v) => v === -1 ? 'Unlimited peer practice sessions'   : `${v} peer practice session${v !== 1 ? 's' : ''}/month`,
                        jobApplicationsPerDay: (v) => v === -1 ? 'Unlimited job applications/day'     : `${v} job application${v !== 1 ? 's' : ''}/day`,
                        aiHintsPerSession:     (v) => v === -1 ? 'Unlimited AI hints per session'     : `${v} AI hint${v !== 1 ? 's' : ''}/session`,
                        sessionDurationMinutes:(v) => v === -1 ? 'Unlimited session duration'         : `Up to ${v}-minute sessions`,
                        maxStorageMb:          (v) => v === -1 ? 'Unlimited storage'                  : `${v >= 1024 ? `${v / 1024} GB` : `${v} MB`} storage`,
                      };

                      // Convert limits object → readable strings
                      const limitItems = Object.entries(plan.limits || {})
                        .filter(([, val]) => val !== 0)          // skip explicitly disabled features
                        .map(([key, val]) => {
                          const formatter = limitLabelMap[key];
                          return formatter ? formatter(val) : null;
                        })
                        .filter(Boolean);

                      // Merge features + limit items, removing duplicates (case-insensitive)
                      const baseFeatures = Array.isArray(plan.features) ? plan.features : [];
                      const seen = new Set(baseFeatures.map((f) => f.toLowerCase()));
                      const dedupedLimits = limitItems.filter((item) => {
                        const key = item.toLowerCase();
                        if (seen.has(key)) return false;
                        seen.add(key);
                        return true;
                      });

                      // Limits first so quotas are prominent, then remaining feature strings
                      const allItems = [...dedupedLimits, ...baseFeatures];

                      return allItems.map((item, index) => (
                        <ListItem key={index} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircle color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={item}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ));
                    })()}
                  </List>

                  {/* Payment Methods */}
                  {pricing && pricing.amount > 0 && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="body2" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Payment Options:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {selectedCurrency === 'INR' && (
                          <>
                            <Chip label="UPI" size="small" variant="outlined" />
                            <Chip label="Cards" size="small" variant="outlined" />
                            <Chip label="Net Banking" size="small" variant="outlined" />
                          </>
                        )}
                        <Chip label="PayPal" size="small" variant="outlined" />
                        <Chip label="Credit Card" size="small" variant="outlined" />
                      </Box>
                    </Box>
                  )}
                </CardContent>

                <CardActions sx={{ p: 3, pt: 0 }}>
                  <Button
                    fullWidth
                    variant={isCurrent ? 'outlined' : 'contained'}
                    color={getPlanColor(plan.name)}
                    size="large"
                    disabled={isCurrent}
                    onClick={() => handleSubscribe(plan)}
                    sx={{ py: 1.5 }}
                  >
                    {isCurrent ? 'Current Plan' : `Choose ${plan.name}`}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Additional Info */}
      <Box sx={{ mt: 8 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, textAlign: 'center', height: '100%' }}>
              <Security sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Secure Payments
              </Typography>
              <Typography variant="body2" color="text.secondary">
                PCI DSS compliant with 256-bit SSL encryption. Your payment data is always protected.
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, textAlign: 'center', height: '100%' }}>
              <Language sx={{ fontSize: 48, color: 'secondary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Global Support
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Multiple currencies and payment methods. Regional pricing for better affordability.
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, textAlign: 'center', height: '100%' }}>
              <Verified sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Compliance Ready
              </Typography>
              <Typography variant="body2" color="text.secondary">
                RBI, GDPR, and SOX compliant. Enterprise-grade security and data protection.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ mt: 8, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
          Frequently Asked Questions
        </Typography>
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Can I change my plan anytime?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                What payment methods do you accept?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                We accept UPI, cards, net banking (India), PayPal, and major credit cards globally.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Is my payment information secure?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Yes, we use industry-standard encryption and are PCI DSS Level 1 compliant.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Do you offer refunds?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                New paid subscriptions may qualify for our advertised 30-day money-back guarantee
                (subject to terms at purchase). Otherwise fees are non-refundable except as required
                by law or marketplace cancellation rules.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Payment Dialog */}
      <Dialog 
        open={paymentDialog.open} 
        onClose={() => setPaymentDialog({ open: false, plan: null })}
        maxWidth="md"
        fullWidth
        aria-labelledby="payment-dialog-title"
      >
        <DialogTitle id="payment-dialog-title">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Complete Your Subscription</Typography>
            <IconButton onClick={() => setPaymentDialog({ open: false, plan: null })}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {paymentDialog.plan && (
            <EnhancedPayment
              planId={paymentDialog.plan.id}
              planName={paymentDialog.plan.name}
              onSuccess={handlePaymentSuccess}
              onClose={() => setPaymentDialog({ open: false, plan: null })}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Close Button */}
      {onClose && (
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Button variant="outlined" onClick={onClose} size="large">
            Close
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default EnhancedPricingPage;